import { logAndNotifyError } from "../modules/logger";
import Notify from "devextreme/ui/notify";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { get, getUserInfo, post } from "../modules/server";
import { Button, Card } from "react-bootstrap";

import { EventInfo } from "devextreme/events";
import { DataGrid } from "devextreme-react";

import ArrayStore from "devextreme/data/array_store";
import dxDataGrid, { EditingStartEvent, NewRowInfo, RowDblClickEvent } from "devextreme/ui/data_grid";
import { Editing, Form, Item, Popup, Toolbar } from "devextreme-react/data-grid";
import { getRoleName, getRoleOrder } from "../modules/common";

import { ConfirmDialogEvent } from "../events/ConfirmDialogEvent";

const Users = () => {
  const [users, setUsers] = useState<ArrayStore<any, any>>();
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isNewRow, setIsNewRow] = useState(false);

  const defaultData = useMemo(() => {
    const dft: any = {};
    for (var i = 0; i < roles.length; i++) {
      dft["role_" + roles[i]] = false;
    }
    return dft;
  }, [roles]);

  const refLastFailedUser = useRef(null);

  const columns = useMemo(() => {
    const btnNewWelcomeMail = {
      text: "Nytt välkomstmail",
      icon: "email",
      hint: "Skicka ett nytt välkomstmail",
      disabled: function (e: any) {
        return !e.row.data.isPrimaryCustomer;
      },
      onClick: async function (e: any) {
        var userId = e.row.data.id;

        const result = await post("Admin/SendWelcomeMail", { userId: userId });
        if (result.success) Notify("Ett nytt välkomstmail har skickats. Kontrollera skräppost om det inte dyker upp i inkorgen.", "success", 8000);
        else logAndNotifyError("Ett fel uppstod när välkomstmailet skulle skickas : " + result.error);
      },
    };
    const btnCreateNewPassword = {
      text: "Nytt lösenord",
      icon: "lock",
      hint: "Skapa nytt lösenord",
      disabled: function (e: any) {
        return !e.row.data.isPrimaryCustomer;
      },
      onClick: async function (e: any) {
        const userId = e.row.data.id;

        const confirmEvent = new ConfirmDialogEvent("Bekräfta", "Vill du verkligen skapa ett nytt lösenord?", { timeoutMs: 30000 });
        PubSub.publish("ui/confirm", confirmEvent);
        const confirmed = await confirmEvent.promise;
        if (!confirmed) return;

        const result = await post("Admin/SetRandomPassword", { userId: userId });
        if (result.success) {
          Notify("Nytt lösenord har skapats", "success", 8000);
          navigator.clipboard.writeText(result.password);
          const confirmEvent = new ConfirmDialogEvent("Nytt lösenord", `Det nya lösenordet är <b>${result.password}</b><br/><br/><br/>Lösenordet har kopierats till klippbordet.`, {
            timeoutMs: 30000,
          });
          PubSub.publish("ui/confirm", confirmEvent);
        } else logAndNotifyError("Ett fel uppstod när lösenordet skulle skapas : " + result.error);
      },
    };

    const allRoles: any[] = [];
    const fields: any[] = [
      { dataField: "id", visible: false, showInColumnChooser: false },
      { dataField: "email", caption: "E-post", fixedPosition: "left" },
      { dataField: "emailConfirmed", visible: true, caption: "E-post bekräftad" },
      { dataField: "phoneNumber", visible: true, caption: "Telefon-nummer" },
      //{ dataField: "password", visible: false, caption: "Lösenord" },
      { caption: "Rättigheter", columns: allRoles },
      {
        type: "buttons",
        // width: 300,
        buttons: [{ name: "delete", icon: "remove", hint: "Ta bort" }, { name: "edit", icon: "edit", hint: "Redigera" }, btnNewWelcomeMail, btnCreateNewPassword],
      },
    ];

    for (var i = 0; i < roles.length; i++) {
      allRoles.push({ dataField: "role_" + roles[i], caption: getRoleName(roles[i]), dataType: "boolean" });
    }

    return fields;
  }, [roles]);

  const items = useMemo(() => {
    const allRoles: any[] = [];
    const fields: any[] = [
      { dataField: "id", visible: false },
      //{ dataField: "password", visible: false, validationRules: [{ type: "required" }] },
      { itemType: "empty", visible: false, tag: "beforePermissions" },
      {
        dataField: "email",
        readOnly: true,
        validationRules: [
          { type: "required", message: "Email måste anges" },
          { type: "email", message: "Email-adressen är ogiltig" },
        ],
      },
      { dataField: "phoneNumber", visible: true },
      { dataField: "emailConfirmed", visible: false },

      { itemType: "group", caption: "Rättigheter", items: allRoles },
    ];

    for (var i = 0; i < roles.length; i++) {
      allRoles.push({ dataField: "role_" + roles[i], caption: getRoleName(roles[i]), dataType: "boolean" });
    }

    return fields;
  }, [roles]);

  const onInitNewRow = useCallback(
    (e: EventInfo<dxDataGrid<any, any>> & NewRowInfo<any>) => {
      setIsNewRow(true);
      e.data = refLastFailedUser.current || defaultData;
    },
    [setIsNewRow, defaultData]
  );

  const onEditingStart = useCallback(
    (e: EditingStartEvent) => {
      setIsNewRow(false);
    },
    [setIsNewRow]
  );

  const customizeItem = useCallback(
    (item: any) => {
      if (isNewRow) {
        item.visible = ["email", "phoneNumber"].includes(item.dataField);
      } else {
        item.visible = ["email", "phoneNumber"].includes(item.dataField);
        item.editorOptions = {
          ...item.editorOptions,
          readOnly: ["email", "emailConfirmed"].includes(item.dataField),
        };
        // if (item.tag === "beforePermissions") {
        //   item.visible = true;
        // }
      }
      if (item.caption === "Rättigheter") item.visible = true;
      if (item.dataField && (item.dataField as string).startsWith("role_")) item.visible = true;
    },
    [isNewRow]
  );

  useEffect(() => {
    fetchUsers();
  }, []);

  // const def: IMutableRef = { instance: undefined };
  // const selfref = useRef(def);

  const fetchUsers = async () => {
    setIsLoading(true);
    var result = await get("Admin/Users");

    var users = result.users;
    var roles = result.roles;
    for (let i = 0; i < users.length; i++) {
      const user = users[i];
      for (var j = 0; j < roles.length; j++) {
        const role = roles[j];
        user["role_" + role] = user.roles.includes(role);
      }
    }
    roles.sort((a: string, b: string) => -getRoleOrder(a).localeCompare(getRoleOrder(b)));

    const ds = new ArrayStore({
      data: users,
      key: "id",
      onUpdated: async function (id) {
        const item = result.users.find((r: any) => r.id === id);
        const roles: string[] = [];
        for (let prop in item) {
          if (prop.startsWith("role_")) {
            if (item[prop]) roles.push(prop.substring(5));
          }
        }
        item.roles = roles;
        const saveResp = await post("Admin/UpdateUser", item);
        if (saveResp.success) Notify("Ändringarna har sparats", "success");
        else logAndNotifyError("Ett fel uppstod när ändringarna skule sparas : " + saveResp.error);
      },
      onRemoved: async function (id) {
        const deleteResp = await get("Admin/RemoveUser/" + id);
        if (!deleteResp.success) {
          logAndNotifyError("Ett fel uppstod när användaren skulle tas bort : " + deleteResp.error);
          fetchUsers();
        }
      },
      onInserted: async function (item) {
        const roles: string[] = [];
        for (let prop in item) {
          if (prop.startsWith("role_")) {
            if (item[prop]) roles.push(prop.substring(5));
          }
        }
        item.roles = roles;

        const saveResp = await post("Admin/NewUser", item);

        if (saveResp.success) {
          item.id = saveResp.userId;
          refLastFailedUser.current = null;
          Notify("Användaren har lagts till. Denne kommer att få ett välkomstmail (Kontrollera skräppost) för att bekräfta sin registrering och skapa ett lösenord.", "success", 8000);
        } else {
          refLastFailedUser.current = item;
          logAndNotifyError("Ett fel uppstod när ändringarna skule sparas : " + saveResp.error);
        }
        fetchUsers();
      },
    });

    setUsers(ds);
    setRoles(roles);
    setIsLoading(false);
  };

  const onRowDblClick = useCallback((e: RowDblClickEvent) => {
    var dg = e.component;
    var rowIndex = e.rowIndex;
    dg.editRow(rowIndex);
  }, []);

  const onCellPrepared = useCallback((e: any) => {
    const userId = getUserInfo()?.userId;
    if (e.column.type === "buttons" && e.rowType === "data") {
      if (e.row.data.id === userId) {
        for (var i = 0; i < e.cellElement.children.length; i++) {
          const child = e.cellElement.children[i];
          if (child?.ariaLabel === "Delete") {
            child.style.visibility = "hidden";
          }
        }
      }
    }
  }, []);

  return (
    <>
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <>
          <Card>
            <Card.Header>Hantera användare</Card.Header>
            <Card.Body>
              <DataGrid
                height={700}
                dataSource={users}
                selection={{ mode: "single" }}
                paging={{ enabled: false }}
                scrolling={{ mode: "virtual" }}
                onInitNewRow={onInitNewRow}
                onRowDblClick={onRowDblClick}
                onEditingStart={onEditingStart}
                onCellPrepared={onCellPrepared}
                columns={columns}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                showBorders={false}
                columnChooser={{ enabled: true }}
                columnFixing={{ enabled: true }}
                stateStoring={{
                  enabled: true,
                  type: "localStorage",
                  storageKey: "QWTool_Users",
                }}
              >
                <Toolbar>
                  <Item name="addRowButton" location="after" />
                  <Item name="columnChooserButton" location="after" />
                  <Item location="after">
                    <Button
                      onClick={() => {
                        PubSub.publish("ShowHelp", "help_Users");
                      }}
                    >
                      Hjälp
                    </Button>
                  </Item>
                </Toolbar>
                <Editing mode="popup" allowUpdating={true} allowAdding={true} allowDeleting={true}>
                  <Form customizeItem={customizeItem} items={items}></Form>
                  <Popup title={"Användare"} showTitle={true} />
                </Editing>
              </DataGrid>
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
};

export default Users;
